import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import FieldTemplatesLibrary from './FieldTemplatesLibrary';

const ElementLibrary = ({ onElementClick, onTemplateClick }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  const elementCategories = [
    {
      id: 'templates',
      name: 'Field Templates',
      icon: 'Layers',
      isTemplate: true
    },
    {
      id: 'input',
      name: 'Input Fields',
      icon: 'Type',
      elements: [
        { id: 'text', name: 'Text Input', icon: 'Type', type: 'input' },
        { id: 'email', name: 'Email', icon: 'Mail', type: 'input' },
        { id: 'phone', name: 'Phone', icon: 'Phone', type: 'input' },
        { id: 'number', name: 'Number', icon: 'Hash', type: 'input' },
        { id: 'url', name: 'URL', icon: 'Link', type: 'input' },
        { id: 'textarea', name: 'Paragraph', icon: 'AlignLeft', type: 'textarea' },
        { id: 'button', name: 'Button', icon: 'MousePointer', type: 'button' }
      ]
    },
    {
      id: 'selection',
      name: 'Selection Fields',
      icon: 'CheckSquare',
      elements: [
        { id: 'dropdown', name: 'Dropdown', icon: 'ChevronDown', type: 'select' },
        { id: 'radio', name: 'Radio Buttons', icon: 'Circle', type: 'radio' },
        { id: 'checkbox', name: 'Checkboxes', icon: 'CheckSquare', type: 'checkbox' },
        { id: 'toggle', name: 'Toggle Switch', icon: 'ToggleLeft', type: 'toggle' },
        { id: 'slider', name: 'Slider', icon: 'SlidersHorizontal', type: 'slider' },
        { id: 'rating', name: 'Star Rating', icon: 'Star', type: 'rating' },
        { id: 'scale', name: 'Numeric Scale', icon: 'BarChart2', type: 'scale' }
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced Fields',
      icon: 'Sparkles',
      elements: [
        { id: 'file', name: 'File Upload', icon: 'Upload', type: 'file' },
        { id: 'signature', name: 'Signature', icon: 'PenTool', type: 'signature' },
        { id: 'hidden', name: 'Hidden Field', icon: 'EyeOff', type: 'hidden' }
      ]
    },
    {
      id: 'layout',
      name: 'Layout Elements',
      icon: 'Layout',
      elements: [
        { id: 'section', name: 'Section', icon: 'Box', type: 'layout' },
        { id: 'columns', name: 'Columns', icon: 'Columns', type: 'layout' },
        { id: 'divider', name: 'Divider', icon: 'Minus', type: 'layout' },
        { id: 'heading', name: 'Heading', icon: 'Heading', type: 'content' },
        { id: 'paragraph', name: 'Paragraph', icon: 'AlignLeft', type: 'content' }
      ]
    },
    {
      id: 'media',
      name: 'Images & Visual',
      icon: 'Image',
      elements: [
        { id: 'image', name: 'Logo/Image', icon: 'Image', type: 'media' },
        { id: 'icon', name: 'Icon', icon: 'Smile', type: 'media' },
        { id: 'progress', name: 'Progress Bar', icon: 'TrendingUp', type: 'visual' },
        { id: 'stepper', name: 'Step Indicator', icon: 'GitBranch', type: 'visual' }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev?.[categoryId]
    }));
  };

  const handleElementClick = (element) => {
    if (onElementClick) {
      onElementClick(element);
    }
  };

  const handleTemplateClick = (template) => {
    if (onTemplateClick) {
      onTemplateClick(template);
    }
  };

  return (
    <div className={`
      bg-card border-r border-border transition-smooth flex-shrink-0 overflow-hidden
      ${isCollapsed ? 'w-16' : 'w-64'}
    `}>
      <div className="h-full flex flex-col overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <h2 className="text-base font-heading font-semibold text-foreground">
                Elements
              </h2>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label={isCollapsed ? 'Expand library' : 'Collapse library'}
            >
              <Icon
                name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
                size={20}
                color="var(--color-muted-foreground)"
              />
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="flex-1 overflow-y-auto scrollbar-custom">
            {elementCategories?.map((category) => {
              const isOpen = openCategories?.[category?.id] || false;
              return (
                <div key={category?.id} className="border-b border-border last:border-b-0">
                  {/* Category Header / Dropdown Toggle */}
                  <button
                    onClick={() => toggleCategory(category?.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 text-sm font-medium
                      transition-smooth hover:bg-muted
                      ${isOpen ? 'text-primary bg-primary/5' : 'text-foreground'}
                    `}
                  >
                    <Icon
                      name={category?.icon}
                      size={16}
                      color={isOpen ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                    <span className="flex-1 text-left" style={{ color: '#117473' }}>{category?.name}</span>
                    <Icon
                      name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                      size={14}
                      color={isOpen ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                  </button>

                  {/* Category Content */}
                  {isOpen && (
                    <div className="px-3 pb-3 pt-1">
                      {category?.isTemplate ? (
                        <FieldTemplatesLibrary onTemplateClick={handleTemplateClick} />
                      ) : (
                        <div className="space-y-1.5">
                          {category?.elements?.map((element) => (
                            <div
                              key={element?.id}
                              onClick={() => handleElementClick(element)}
                              className="
                                flex items-center gap-3 py-1.5 px-3 bg-muted/50 rounded-md
                                transition-smooth border border-transparent hover:border-border
                                cursor-pointer hover:bg-primary/10 hover:shadow-elevation-sm
                              "
                            >
                              <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                                <Icon name={element?.icon} size={15} color="#007876" />
                              </div>
                              <span className="text-sm font-medium flex-1" style={{ color: '#007876' }}>
                                {element?.name}
                              </span>
                              <Icon name="Plus" size={14} color="var(--color-primary)" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isCollapsed && (
          <div className="flex-1 overflow-y-auto scrollbar-custom p-2">
            <div className="space-y-2">
              {elementCategories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => {
                    setIsCollapsed(false);
                    setOpenCategories(prev => ({ ...prev, [category?.id]: true }));
                  }}
                  className="w-full p-2 rounded-md transition-smooth hover:bg-muted"
                  title={category?.name}
                >
                  <Icon
                    name={category?.icon}
                    size={20}
                    color="var(--color-muted-foreground)"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElementLibrary;